// ============================================================================
// 系统设置服务
// 用于管理系统设置的获取和缓存
// ============================================================================

import axios from 'axios'

class SettingsService {
  constructor() {
    this.cache = new Map()
    this.cacheExpiry = new Map()
    this.cacheDuration = 5 * 60 * 1000 // 5分钟缓存
  }

  // ============================================================================
  // 缓存管理
  // ============================================================================

  isCacheValid(key) {
    const expiry = this.cacheExpiry.get(key)
    return expiry && Date.now() < expiry
  }

  setCache(key, data) {
    this.cache.set(key, data)
    this.cacheExpiry.set(key, Date.now() + this.cacheDuration)
  }

  getCache(key) {
    if (this.isCacheValid(key)) {
      return this.cache.get(key)
    }
    return null
  }

  clearCache(key = null) {
    if (key) {
      this.cache.delete(key)
      this.cacheExpiry.delete(key)
    } else {
      this.cache.clear()
      this.cacheExpiry.clear()
    }
  }

  // ============================================================================
  // API调用
  // ============================================================================

  // 获取公开的系统设置（不需要认证）
  async getPublicSettings() {
    const cacheKey = 'public_settings'
    const cached = this.getCache(cacheKey)
    if (cached) {
      return cached
    }

    try {
      const response = await axios.get('/api/settings/public')
      console.log('获取设置响应:', response)
      if (response.success) {
        this.setCache(cacheKey, response.data)
        return response.data
      }
      throw new Error(response.message || '获取系统设置失败')
    } catch (error) {
      console.error('获取公开系统设置失败:', error)
      // 返回默认设置
      return {
        site_name: '永盛制冷维修有限公司',
        company_phone: '',
        company_address: '',
        service_hours: '8:00-18:00',
        icp_record: '',
        copyright_text: '© 2025 永盛制冷维修有限公司 版权所有',
        system_notice: ''
      }
    }
  }

  // 获取所有系统设置（需要管理员权限）
  async getAllSettings() {
    const cacheKey = 'all_settings'
    const cached = this.getCache(cacheKey)
    if (cached) {
      return cached
    }

    try {
      const response = await axios.get('/api/settings/', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.success) {
        this.setCache(cacheKey, response.data)
        return response.data
      }
      throw new Error(response.message || '获取系统设置失败')
    } catch (error) {
      console.error('获取系统设置失败:', error)
      throw error
    }
  }

  // 获取特定分类的设置
  async getCategorySettings(category) {
    const cacheKey = `category_${category}`
    const cached = this.getCache(cacheKey)
    if (cached) {
      return cached
    }

    try {
      const response = await axios.get(`/api/settings/category/${category}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.success) {
        this.setCache(cacheKey, response.data)
        return response.data
      }
      throw new Error(response.message || '获取设置失败')
    } catch (error) {
      console.error(`获取${category}设置失败:`, error)
      throw error
    }
  }

  // 批量更新系统设置
  async updateSettings(settings) {
    try {
      const response = await axios.put('/api/settings/', settings, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.success) {
        // 清除缓存，强制重新获取
        this.clearCache()
        return response
      }
      throw new Error(response.message || '更新设置失败')
    } catch (error) {
      console.error('更新系统设置失败:', error)
      throw error
    }
  }

  // 更新单个设置
  async updateSetting(key, value) {
    try {
      const response = await axios.put(`/api/settings/${key}`, { value }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.success) {
        // 清除相关缓存
        this.clearCache()
        return response
      }
      throw new Error(response.message || '更新设置失败')
    } catch (error) {
      console.error('更新单个设置失败:', error)
      throw error
    }
  }

  // ============================================================================
  // 便捷方法
  // ============================================================================

  // 获取网站标题
  async getSiteName() {
    const settings = await this.getPublicSettings()
    return settings.site_name || '永盛制冷维修有限公司'
  }

  // 获取备案号
  async getIcpRecord() {
    const settings = await this.getPublicSettings()
    return settings.icp_record || ''
  }

  // 获取公司电话
  async getCompanyPhone() {
    const settings = await this.getPublicSettings()
    return settings.company_phone || ''
  }

  // 获取公司地址
  async getCompanyAddress() {
    const settings = await this.getPublicSettings()
    return settings.company_address || ''
  }

  // 获取版权信息
  async getCopyrightText() {
    const settings = await this.getPublicSettings()
    return settings.copyright_text || '© 2025 永盛制冷维修有限公司 版权所有'
  }

  // 获取系统公告
  async getSystemNotice() {
    const settings = await this.getPublicSettings()
    return settings.system_notice || ''
  }

  // 获取服务时间
  async getServiceHours() {
    const settings = await this.getPublicSettings()
    return settings.service_hours || '8:00-18:00'
  }

  // 检查是否维护模式
  async isMaintenanceMode() {
    try {
      const settings = await this.getAllSettings()
      return settings.system?.maintenance_mode?.value || false
    } catch (error) {
      return false
    }
  }

  // 检查是否允许注册
  async isRegistrationAllowed() {
    try {
      const settings = await this.getAllSettings()
      return settings.system?.allow_registration?.value !== false
    } catch (error) {
      return true
    }
  }
}

// 创建单例实例
const settingsService = new SettingsService()

export default settingsService